import type { PortableTextBlock } from "@portabletext/types";

import styles from "@brexhq/shared/components/Typography/styles";
import { breakpointValues } from "@brexhq/shared/styles/breakpoints";
import { SanityTextRowProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import { ThemeTextComponents } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";

export type CustomComponentBlock = {
  _type: string;
  _key: string;
  [key: string]: unknown;
};

export type ProcessedSpan = {
  _type: "span";
  text: string;
  _key?: string;
  marks?: string[];
};

export type ProcessedBlockContent =
  | PortableTextBlock
  | CustomComponentBlock
  | ProcessedSpan;

/**
 * Generic function to process block content and replace string patterns with custom components
 *
 * @param blocks - Array of block content to process
 * @param replacements - Array of replacement configurations
 * @returns Processed block content with custom components inserted
 *
 * @example
 * // Replace arrows with custom components
 * processBlockContentForCustomComponents(blocks, [
 *   { pattern: "->", componentType: "arrowRightMono" }
 * ]);
 *
 * @example
 * // Replace multiple patterns with different components
 * processBlockContentForCustomComponents(blocks, [
 *   { pattern: "->", componentType: "arrowRightMono" },
 *   { pattern: "=>", componentType: "doubleArrow", componentProps: { size: "large" } },
 *   { pattern: "★", componentType: "starIcon", componentProps: { color: "gold" } }
 * ]);
 *
 * @example
 * // Replace with components that have additional properties
 * processBlockContentForCustomComponents(blocks, [
 *   {
 *     pattern: "🔗",
 *     componentType: "linkIcon",
 *     componentProps: {
 *       href: "https://example.com",
 *       target: "_blank"
 *     }
 *   }
 * ]);
 */
export function processBlockContentForCustomComponents(
  blocks: ProcessedBlockContent[],
  replacements: Array<{
    pattern: string;
    componentType: string;
    componentProps?: Record<string, unknown>;
  }>,
): ProcessedBlockContent[] {
  if (!blocks || !Array.isArray(blocks) || !replacements.length) return blocks;

  return blocks.map((block) => {
    if (block._type !== "block" || !("children" in block) || !block.children) {
      return block;
    }

    const processedChildren = (block.children as any[]).flatMap((child) => {
      if (child._type !== "span" || typeof child.text !== "string") {
        return child;
      }

      const matchingReplacement = replacements.find((replacement) =>
        child.text.includes(replacement.pattern),
      );

      if (!matchingReplacement) {
        return child;
      }

      const {
        pattern,
        componentType,
        componentProps = {},
      } = matchingReplacement;
      const textParts = child.text.split(pattern);

      if (textParts.length <= 1) {
        return child;
      }

      return textParts.reduce((acc: ProcessedBlockContent[], part, index) => {
        if (part) {
          if (child._type === "span" && "text" in child) {
            acc.push({
              _type: "span",
              text: part,
              _key: child._key,
              marks: child.marks,
            });
          }
        }

        if (index < textParts.length - 1) {
          acc.push({
            _type: componentType,
            _key: `${componentType}-${index}`,
            ...componentProps,
          });
        }

        return acc;
      }, []);
    });

    return {
      ...block,
      children: processedChildren,
    };
  });
}

export const getInitialFontSize = (
  textStyle: SanityTextRowProps["textStyle"],
  windowWidth: number,
) => {
  const { LRG, MED } = breakpointValues;
  const textComponent = ThemeTextComponents[textStyle];

  if (!textComponent) {
    return null;
  }

  const { components } = textComponent;

  const componentKey =
    windowWidth >= LRG ? "LRG" : windowWidth >= MED ? "MED" : "SML";

  const styleKey = components[componentKey];

  return styles[styleKey]?.fontSize || null;
};
